import {DependencyList, useEffect} from "react";
import {noop} from "./FunctionUtil";

/**
 * Lifetime pattern for managing resource lifecycle and automatic cleanup.
 *
 * A Lifetime represents the lifecycle of a component or feature. When terminated,
 * all resources (subscriptions, listeners, timers, etc.) registered to it are
 * automatically cleaned up.
 *
 * @example
 * const lifetime = new Lifetime();
 *
 * // Register cleanup callbacks
 * lifetime.onTermination(() => subscription.unsubscribe());
 * lifetime.onTermination(() => clearInterval(timerId));
 *
 * // Create nested lifetimes
 * const nested = lifetime.createNested();
 *
 * // Terminate (triggers all callbacks)
 * lifetime.terminate();
 */
export class Lifetime {
    private callbacks: (() => void)[] = [];
    private terminated = false;

    /**
     * Registers a callback to be executed when this lifetime is terminated.
     * If the lifetime is already terminated, the callback is executed immediately.
     *
     * @param callback Function to execute on termination
     */
    onTermination(callback: () => void): () => void {
        if (this.terminated) {
            callback();
            return noop;
        }
        this.callbacks.push(callback);
        return () => {
            const index = this.callbacks.indexOf(callback);
            if (index >= 0) {
                this.callbacks.splice(index, 1);
            }
        };
    }

    /**
     * Creates a nested (child) lifetime. The child lifetime will be automatically
     * terminated when the parent is terminated.
     *
     * @returns A new nested Lifetime instance
     */
    nested(): Lifetime {
        if (this.terminated) {
            const nested = new Lifetime();
            nested.terminate();
            return nested;
        }

        const nested = new Lifetime();
        const unsubscribe = this.onTermination(() => nested.terminate());
        nested.onTermination(unsubscribe);
        return nested;
    }

    /**
     * Terminates this lifetime, executing all registered callbacks in LIFO order
     * (last registered, first executed) and terminating all child lifetimes.
     * Subsequent calls to terminate() are ignored.
     */
    terminate(): void {
        if (this.terminated) {
            return;
        }

        this.terminated = true;

        // Execute callbacks in reverse order (LIFO)
        while (this.callbacks.length > 0) {
            const callback = this.callbacks.pop()!;
            try {
                callback();
            } catch (error) {
                console.error("Error during lifetime termination:", error);
            }
        }
    }

    /**
     * Returns whether this lifetime has been terminated.
     */
    get isTerminated(): boolean {
        return this.terminated;
    }

    /**
     * Returns whether this lifetime is still alive (not terminated).
     */
    get isAlive(): boolean {
        return !this.terminated;
    }

    /**
     * A lifetime that is already terminated. Useful as a sentinel value.
     */
    static Terminated: Lifetime = (() => {
        const lifetime = new Lifetime();
        lifetime.terminate();
        return lifetime;
    })();


    static Infinite: Lifetime = new (class extends Lifetime {
        onTermination(callback: () => void): () => void {
            // Ignore subscription
            return noop;
        }

        nested(): Lifetime {
            // Just a lifetime, no parent
            return new Lifetime();
        }

        terminate() {
            // Ignore termination, it's immortal
        }

        get isTerminated(): boolean {
            return false;
        }

        get isAlive(): boolean {
            return true;
        }
    })();
}

/**
 * Helper to bracket a lifetime for a specific scope. Returns a disposable
 * function that terminates the lifetime when called.
 *
 * @example
 * const dispose = withLifetime(lifetime => {
 *   lifetime.onTermination(() => subscription.unsubscribe());
 * });
 *
 * // Later...
 * dispose();
 */
export function withLifetime(setup: (lifetime: Lifetime) => void): () => void {
    const lifetime = new Lifetime();
    setup(lifetime);
    return () => lifetime.terminate();
}

export class LifetimeSequence {
    private currentLifetime: Lifetime | undefined = undefined;

    constructor(private readonly parentLifetime: Lifetime) {
    }

    next(): Lifetime {
        if (this.currentLifetime) {
            this.currentLifetime.terminate();
        }
        this.currentLifetime = this.parentLifetime.nested();
        return this.currentLifetime;
    }
}

export function moduleLifetime(module: NodeJS.Module): Lifetime {
    const lifetime = new Lifetime();
    const hot = (module as any).hot;
    if (hot) {
        hot.dispose(() => lifetime.terminate());
    }
    return lifetime;
}

export function listenWindowEvent<K extends keyof WindowEventMap>(lifetime: Lifetime, type: K, listener: (this: Window, ev: WindowEventMap[K]) => any, options?: boolean | AddEventListenerOptions) {
    window.addEventListener(type, listener, options);
    lifetime.onTermination(() => {
        window.removeEventListener(type, listener, options)
    });
}

export function listenCustomWindowEvent<E extends CustomEvent<any>>(lifetime: Lifetime, type: string, listener: (this: Window, ev: E) => any, options?: boolean | AddEventListenerOptions) {
    window.addEventListener(type, listener as EventListener, options);
    lifetime.onTermination(() => {
        window.removeEventListener(type, listener as EventListener, options);
    });
}

export function listenDocumentEvent<K extends keyof DocumentEventMap>(lifetime: Lifetime, type: K, listener: (this: Document, ev: DocumentEventMap[K]) => any, options?: boolean | AddEventListenerOptions) {
    document.addEventListener(type, listener, options);
    lifetime.onTermination(() => {
        document.removeEventListener(type, listener, options)
    });
}

export function useEffectWithLifetime(effect: (lifetime: Lifetime) => void, deps?: DependencyList) {
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        return withLifetime(effect);
    }, deps);
    /* eslint-enable react-hooks/exhaustive-deps */
}
